import { DependencyList, KeyboardEventHandler, useEffect, useRef } from 'react';
import { settingsStore, useSettingsSelector } from '../store/SettingsStore';
import { compareCaseInsensitive } from './Text';

/** Listener for a single key press */
export function useKeyPress(key: string, callback: () => unknown) {
	const enabled = useSettingsSelector(settingsStore.selectors.gameControlKeysEnabled);
	const callbackRef = useRef(callback);

	// Use a ref to avoid potentially add/removing event listeners every render
	useEffect(() => {
		callbackRef.current = callback;
	}, [callback]);

	useEffect(function setupKeyboardHandler() {
		function handleKeyPress(event: KeyboardEvent) {
			if (compareCaseInsensitive(event.key, key)) {
				event.preventDefault();
				callbackRef.current();
			}
		}

		if (enabled) {
			window.addEventListener('keydown', handleKeyPress);
			return () => window.removeEventListener('keydown', handleKeyPress);
		}
	}, [key]);
}

/**
 * General purpose keyboard handler
 * Callback should return true if the event was handled.
 */
export function useKeyPressHandler(handler: (key: string) => boolean, dependencies: DependencyList) {
	const enabled = useSettingsSelector(settingsStore.selectors.gameControlKeysEnabled);
	const callbackRef = useRef(handler);

	// Use a ref to avoid potentially add/removing event listeners every render
	useEffect(() => {
		callbackRef.current = handler;
	}, [handler]);

	useEffect(function setupKeyboardHandler() {
		function handleKeyPress(event: KeyboardEvent) {
			if (callbackRef.current(event.key)) {
				event.preventDefault();
			}
		}

		if (enabled) {
			window.addEventListener('keydown', handleKeyPress);
			return () => window.removeEventListener('keydown', handleKeyPress);
		}
	}, dependencies);
}

const consume: KeyboardEventHandler = (e) => {
	// Allow escape keys through to close modals
	if (e.key !== 'Escape') {
		e.stopPropagation();
	}
};

/**
 * Partial props that can be passed to e.g. <Dialog> to consume keyboard events before they can propogate to 
 * listeners attached to the window or document, such as in the hooks above.
 */
export const consumeKeyEvents = {
	onKeyDownCapture: consume,
	onKeyUpCapture: consume,
};
